"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useChat } from "../../contexts/ChatContext";
import chatService from "../../services/chatService";
import { format, isValid } from "date-fns";
import {
  FaPaperPlane,
  FaSpinner,
  FaBars,
  FaTimes,
  FaArrowLeft,
  FaEllipsisV,
  FaCheck,
  FaCheckDouble,
} from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import { ToastContainer, toast, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import socketService from "../../utils/socketService";

// Fonction utilitaire pour formater une date de manière sécurisée
const formatDate = (dateString) => {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    return isValid(date) ? format(date, "HH:mm") : "";
  } catch (error) {
    console.error("Erreur de formatage de date:", error);
    return "";
  }
};

const DemenageurChat = () => {
  // Hooks et états
  const { user } = useAuth();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const isMounted = useRef(true);
  const audioRef = useRef(null);

  // Récupérer l'état et les fonctions du contexte de chat
  const {
    conversations = [],
    messages = [],
    isLoading = false,
    currentConversation,
    error,
    showError,
    setMessages,
    setConversations,
    setIsLoading,
    fetchConversations,
    sendMessage: contextSendMessage,
    markAsRead,
    setCurrentConversation,
  } = useChat();

  // États locaux
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const endRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Fonction pour faire défiler vers le bas de la conversation
  const scrollToBottom = useCallback(() => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // Effet de nettoyage
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Charger les conversations au montage
  useEffect(() => {
    if (isMounted.current) {
      console.log("🔄 Chargement des conversations via ChatContext...");
      fetchConversations();
    }
  }, [fetchConversations]);

  // Faire défiler vers le bas quand de nouveaux messages arrivent
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  // Référence pour suivre l'ID de conversation précédent
  const prevConversationIdRef = useRef(conversationId);

  // Mettre à jour la conversation courante quand l'URL change
  useEffect(() => {
    if (conversationId && conversations?.length > 0) {
      const conversationChanged = prevConversationIdRef.current !== conversationId;
      const conv = conversations.find((c) => c?.id === conversationId);

      if (conv) {
        const updatedConversation = {
          ...conv,
          lastMessage: conv.lastMessage || {},
          unreadCount: conv.unreadCount || 0,
        };

        if (!currentConversation || conversationChanged) {
          setCurrentConversation(updatedConversation);
        }
      } else if (process.env.NODE_ENV === "development") {
        console.warn(`⚠️ Conversation non trouvée: ${conversationId}`);
      }

      prevConversationIdRef.current = conversationId;
    }
  }, [conversationId, conversations, setCurrentConversation, currentConversation]);

  // Rediriger vers la première conversation si aucune n'est sélectionnée
  useEffect(() => {
    if (!isLoading && conversations.length > 0 && !conversationId) {
      console.log("🔄 Redirection vers la première conversation");
      navigate(`/demenageur/messages/${conversations[0].id}`, { replace: true });
    }
  }, [conversations, conversationId, isLoading, navigate]);

  // Charger les messages d'une conversation
  const loadMessages = useCallback(
    async (convId) => {
      if (!convId) return false;

      try {
        setIsLoading(true);
        const response = await chatService.getMessages(convId);

        if (!isMounted.current) return false;

        // Gestion des différents formats de réponse
        let msgs = [];
        
        // Cas 1: La réponse est déjà un tableau
        if (Array.isArray(response)) {
          msgs = response;
        } 
        // Cas 2: La réponse a une propriété 'data' qui est un tableau
        else if (response?.data && Array.isArray(response.data)) {
          msgs = response.data;
        }
        // Cas 3: La réponse a une propriété 'messages' qui est un tableau
        else if (response?.messages && Array.isArray(response.messages)) {
          msgs = response.messages;
        }
        // Cas 4: La réponse est un objet avec une propriété 'success' et 'data'
        else if (response?.success === true && Array.isArray(response.data)) {
          msgs = response.data;
        } else {
          console.error("Format de réponse inattendu:", response);
          throw new Error("Format de réponse invalide");
        }

        // Formater les messages
        const formattedMessages = msgs
          .filter((msg) => msg && (msg.id || msg._id))
          .map((msg) => ({
            id: msg.id || msg._id,
            content: msg.content || "",
            sender: msg.sender || {
              id: msg.sender_id || msg.senderId,
              name: msg.sender_name || "Utilisateur inconnu",
              firstName: msg.sender_firstName || msg.sender_first_name || "",
              lastName: msg.sender_lastName || msg.sender_last_name || "",
            },
            senderId: msg.sender_id || msg.senderId || (msg.sender ? msg.sender.id : null),
            senderName: msg.sender_name || (msg.sender ? 
              `${msg.sender.firstName || msg.sender.first_name || ''} ${msg.sender.lastName || msg.sender.last_name || ''}`.trim() : 
              'Utilisateur inconnu'),
            createdAt: msg.created_at || msg.createdAt || new Date().toISOString(),
            read: msg.read !== undefined ? msg.read : (msg.is_read || msg.read_at !== null || false),
            status: msg.status || "delivered",
            conversationId: convId,
          }));

        setMessages(formattedMessages);

        const unreadMessages = formattedMessages
          .filter((m) => m.id && !m.read && m.senderId !== user?.id)
          .map((m) => m.id);

        if (unreadMessages.length > 0) {
          try {
            await chatService.markAsRead(convId, unreadMessages);
            setConversations((prev) => prev.map((conv) => (conv.id === convId ? { ...conv, unreadCount: 0 } : conv)));
          } catch (markReadError) {
            console.error("Erreur lors du marquage des messages comme lus:", markReadError);
          }
        }
        return true;
      } catch (err) {
        console.error("Erreur lors du chargement des messages:", err);
        const errorMessage =
          err.response?.status === 404
            ? "Cette conversation n'existe pas ou vous n'y avez pas accès"
            : "Erreur lors du chargement des messages";
        showError(errorMessage);
        if (err.response?.status === 404) {
          setTimeout(() => navigate("/demenageur/messages"), 3000);
        }
        return false;
      } finally {
        if (isMounted.current) setIsLoading(false);
      }
    },
    [user?.id, setMessages, setConversations, showError, navigate, setIsLoading],
  );

  const handleNewMessage = useCallback(
    (incomingMessage) => {
      if (!incomingMessage || !incomingMessage.id) {
        console.warn("[v1] Invalid message received:", incomingMessage);
        return;
      }

      const message = {
        id: incomingMessage.id || incomingMessage._id,
        content: incomingMessage.content || incomingMessage.message || "",
        conversation_id: incomingMessage.conversation_id || incomingMessage.conversationId,
        sender_id: incomingMessage.sender_id || incomingMessage.senderId,
        created_at: incomingMessage.created_at || incomingMessage.createdAt || new Date().toISOString(),
        is_read: incomingMessage.is_read || incomingMessage.read || false,
        sender_name: incomingMessage.sender_name || "Utilisateur",
      };

      const normalizedMessage = {
        id: message.id,
        content: message.content,
        senderId: message.sender_id,
        senderName: message.sender_name,
        createdAt: message.created_at,
        read: message.is_read,
        status: "delivered",
        conversationId: message.conversation_id,
      };

      if (message.conversation_id === conversationId) {
        setMessages((prevMessages) => {
          const exists = prevMessages.find((m) => m.id === message.id);
          if (exists) return prevMessages;
          return [...prevMessages, normalizedMessage];
        });

        if (message.sender_id !== user?.id) {
          markAsRead(message.conversation_id);
          if (audioRef.current) {
            audioRef.current.play().catch((e) => console.log("Audio play failed:", e));
          }
        }
        setTimeout(() => scrollToBottom(), 100);
      }

      setConversations((prevConversations) => {
        const updatedConversations = [...prevConversations];
        const conversationIndex = updatedConversations.findIndex((c) => c.id === message.conversation_id);
        if (conversationIndex !== -1) {
          updatedConversations[conversationIndex] = {
            ...updatedConversations[conversationIndex],
            lastMessage: normalizedMessage,
            last_message_content: message.content,
            last_message_at: message.created_at,
            updatedAt: new Date().toISOString(),
            unreadCount:
              message.conversation_id === conversationId
                ? 0
                : (updatedConversations[conversationIndex].unreadCount || 0) + 1,
          };
          const [movedConversation] = updatedConversations.splice(conversationIndex, 1);
          updatedConversations.unshift(movedConversation);
        }
        return updatedConversations;
      });
    },
    [conversationId, user?.id, markAsRead, scrollToBottom, setMessages, setConversations],
  );

  // Effet pour gérer le changement de conversation et charger les messages
  useEffect(() => {
    if (conversationId && currentConversation?.id === conversationId) {
      loadMessages(conversationId).then(() => {
        if (markAsRead) markAsRead(conversationId);
      });
    }
  }, [conversationId, currentConversation, loadMessages, markAsRead]);

  // Effet pour la gestion des sockets
  useEffect(() => {
    if (!user?.id) {
      console.warn("[v1] Socket connection cancelled: user not connected");
      return;
    }

    let isComponentMounted = true;
    const initializeSocket = async () => {
      try {
        if (!isComponentMounted) return;

        socketService.on("new_message", handleNewMessage);
        socketService.on("message", handleNewMessage);
        socketService.on("typing", (data) => {
          if (data.conversationId === conversationId && data.userId !== user?.id) {
            setIsTyping(data.isTyping);
            if (data.isTyping) {
              if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
              typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 2000);
            }
          }
        });
        socketService.on("user_status", (data) => {
          if (currentConversation?.client?.id === data.userId) {
            setIsOnline(data.isOnline);
          }
        });
      } catch (error) {
        console.error("[v1] Socket connection failed:", error);
      }
    };
    initializeSocket();

    return () => {
      isComponentMounted = false;
      socketService.off("new_message", handleNewMessage);
      socketService.off("message", handleNewMessage);
      socketService.off("typing");
      socketService.off("user_status");
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [user?.id, conversationId, currentConversation, handleNewMessage]);

  const handleTyping = useCallback(
    (isTypingState) => {
      if (socketService.isConnected && conversationId) {
        socketService.emit("typing", {
          conversationId,
          isTyping: isTypingState,
          userId: user?.id,
        });
      }
    },
    [conversationId, user?.id],
  );

  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setInput(value);

      if (!typingTimeoutRef.current) {
        handleTyping(true);
      } else {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        handleTyping(false);
        typingTimeoutRef.current = null;
      }, 2000);
    },
    [handleTyping],
  );

  const sendMessage = useCallback(
    async (e) => {
      e.preventDefault();

      if (!input.trim() || !conversationId || !contextSendMessage || isSending) return;

      const messageContent = input.trim();
      setInput("");

      try {
        setIsSending(true);
        await contextSendMessage(messageContent, conversationId);
        handleTyping(false);
        setTimeout(() => scrollToBottom(), 100);
      } catch (error) {
        showError("Erreur lors de l'envoi du message");
        setInput(messageContent);
      } finally {
        setIsSending(false);
      }
    },
    [input, conversationId, contextSendMessage, scrollToBottom, handleTyping, showError, isSending],
  );

  const handleConversationSelect = (convId) => {
    navigate(`/demenageur/messages/${convId}`);
    setIsMobileSidebarOpen(false);
  };

  const getMessageStatusIcon = (status, read) => {
    if (status === "sending") {
      return <FaSpinner className="animate-spin text-xs text-amber-500" />;
    }
    if (read) {
      return <FaCheckDouble className="text-xs text-amber-500" />;
    }
    if (status === "delivered") {
      return <FaCheck className="text-xs text-slate-400" />;
    }
    return null;
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-950 via-slate-900 to-gray-900 relative text-amber-100">
      <audio ref={audioRef} src="/message_sound.mp3" preload="auto" />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        transition={Slide}
      />
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <div
        className={`
          ${isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          fixed lg:relative z-50 lg:z-auto
          w-80 lg:w-1/3 xl:w-1/4 2xl:w-1/5
          h-full
          border-r border-amber-500/20 
          bg-gradient-to-b from-slate-900/98 via-gray-900/95 to-slate-800/98 
          backdrop-blur-xl
          flex flex-col 
          shadow-2xl lg:shadow-none
          transition-transform duration-300 ease-in-out
        `}
      >
        <div className="p-4 lg:p-6 border-b border-amber-500/20 bg-gradient-to-r from-slate-800/90 to-gray-800/90 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl lg:text-2xl font-serif font-bold text-amber-100 tracking-wide">Messages</h2>
              <div className="w-12 lg:w-16 h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 mt-2 lg:mt-3 rounded-full shadow-lg"></div>
            </div>
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              className="lg:hidden p-2 text-amber-400 hover:text-amber-300 transition-colors"
            >
              <FaTimes className="text-lg" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {isLoading && conversations.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <FaSpinner className="animate-spin text-amber-500 text-2xl lg:text-3xl" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center p-6 lg:p-8 text-slate-400 font-medium">Aucune conversation</div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                className={`p-4 lg:p-5 border-b border-slate-700/50 hover:bg-gradient-to-r hover:from-slate-700/50 hover:to-gray-700/50 cursor-pointer transition-all duration-300 group ${
                  conversationId === conv.id
                    ? "bg-gradient-to-r from-slate-700/60 to-gray-700/40 border-l-4 border-l-amber-500 shadow-lg"
                    : ""
                }`}
                onClick={() => handleConversationSelect(conv.id)}
              >
                <div className="flex items-center relative">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 lg:h-14 lg:w-14 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 flex items-center justify-center shadow-xl ring-2 ring-amber-400/30">
                      <span className="text-slate-900 font-bold text-base lg:text-lg drop-shadow-sm">
                        {conv.client?.first_name?.[0] || "C"}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3 lg:ml-4 flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <p className="text-sm lg:text-base font-bold text-amber-100 truncate">
                        {conv.client?.first_name || "Client"} {conv.client?.last_name || ""}
                      </p>
                      <p className="text-xs text-amber-400 font-semibold">
                        {conv.last_message_at ? format(new Date(conv.last_message_at), "HH:mm") : ""}
                      </p>
                    </div>
                    <p className="text-xs lg:text-sm text-slate-300 truncate mt-1 leading-relaxed">
                      {conv.last_message_content || "Aucun message"}
                    </p>
                  </div>
                  {(conv.unread_count || 0) > 0 && (
                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 text-xs rounded-full h-5 w-5 lg:h-6 lg:w-6 flex items-center justify-center font-bold shadow-lg ring-2 ring-slate-800">
                      {conv.unread_count}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col bg-gradient-to-b from-slate-800/80 to-gray-900/60 min-w-0">
        {conversationId && currentConversation ? (
          <>
            <div className="p-4 lg:p-6 border-b border-amber-500/20 bg-gradient-to-r from-slate-800/90 via-gray-800/50 to-slate-700/70 backdrop-blur-sm flex items-center shadow-lg">
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className="lg:hidden mr-3 p-2 text-amber-400 hover:text-amber-300 transition-colors"
              >
                <FaBars className="text-lg" />
              </button>

              <div className="flex-shrink-0">
                <div className="h-10 w-10 lg:h-14 lg:w-14 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 flex items-center justify-center shadow-xl ring-2 ring-amber-400/30">
                  <span className="text-slate-900 font-bold text-sm lg:text-lg drop-shadow-sm">
                    {currentConversation.client?.first_name?.[0] || "C"}
                  </span>
                </div>
              </div>
              <div className="ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="font-bold text-amber-100 text-base lg:text-xl truncate">
                  {currentConversation.client?.first_name || "Client"} {currentConversation.client?.last_name || ""}
                </p>
                <p className="text-xs lg:text-sm text-amber-400 font-semibold flex items-center">
                  <span
                    className={`inline-block w-2 h-2 rounded-full mr-2 ${isOnline ? "bg-green-500" : "bg-red-500"}`}
                  ></span>
                  {isTyping ? "En train d'écrire..." : isOnline ? "En ligne" : "Hors ligne"}
                </p>
              </div>
              <button className="text-slate-400 hover:text-amber-400 transition-colors p-2 hidden sm:block">
                <FaEllipsisV className="text-lg" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 lg:p-6 bg-gradient-to-b from-slate-900/40 via-gray-900/30 to-slate-800/40 flex flex-col-reverse messages-container">
              <div ref={endRef} />
              {messages.length === 0 ? (
                <div key="no-messages" className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div key="message-icon" className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-amber-500/20 to-amber-600/30 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl border border-amber-500/20">
                      <span className="text-amber-400 text-xl lg:text-2xl">💬</span>
                    </div>
                    <p key="no-message-text" className="text-slate-300 font-semibold text-base lg:text-lg">Aucun message</p>
                    <p key="start-conversation-text" className="text-slate-400 text-xs lg:text-sm mt-1">Commencez la conversation !</p>
                  </div>
                </div>
              ) : (
                messages.map((message, index) => {
                  const isCurrentUser = message?.sender?.id === user.id || message.senderId === user.id;
                  const messageKey = message.id || `msg-${index}-${Date.now()}`;
                  return (
                    <div
                      key={messageKey}
                      className={`flex mt-4 animate-in fade-in-0 duration-300 ${
                        isCurrentUser ? "justify-end" : "justify-start"
                      }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="max-w-[85%] sm:max-w-[75%] lg:max-w-2xl">
                        <div
                          className={`flex items-start gap-2 lg:gap-3 ${isCurrentUser ? "flex-row-reverse" : "flex-row"}`}
                        >
                          <div className="flex-shrink-0">
                            <div
                              className={`h-8 w-8 lg:h-10 lg:w-10 rounded-full flex items-center justify-center shadow-lg ring-2 ${
                                isCurrentUser
                                  ? "bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 ring-amber-400/30"
                                  : "bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800 ring-amber-500/20"
                              }`}
                            >
                              <span
                                className={`text-xs lg:text-sm font-bold ${
                                  isCurrentUser ? "text-slate-900" : "text-amber-400"
                                }`}
                              >
                                {isCurrentUser
                                  ? user.first_name?.[0] || "M"
                                  : currentConversation.client?.first_name?.[0] || "C"}
                              </span>
                            </div>
                          </div>
                          <div className={`flex flex-col ${isCurrentUser ? "items-end" : "items-start"}`}>
                            <div
                              className={`px-4 lg:px-6 py-3 lg:py-4 rounded-2xl shadow-xl backdrop-blur-sm border transition-all duration-200 hover:shadow-2xl relative ${
                                isCurrentUser
                                  ? "bg-gradient-to-br from-amber-500 via-amber-600 to-amber-700 text-slate-900 rounded-tr-none border-amber-500/30"
                                  : "bg-gradient-to-br from-slate-700/90 via-slate-800/90 to-slate-900/90 text-amber-100 rounded-tl-none border-slate-600/50"
                              }`}
                            >
                              <p className="text-sm lg:text-base leading-relaxed font-medium break-words">
                                {typeof message.content === "string" ? message.content : message.content?.content || ""}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              {isCurrentUser && getMessageStatusIcon(message.status, message.read)}
                              <p
                                className={`text-xs ${
                                  isCurrentUser ? "text-amber-300" : "text-slate-400"
                                }`}
                              >
                                {formatDate(message.createdAt)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="p-3 lg:p-6 border-t border-amber-500/20 bg-gradient-to-r from-slate-800/95 via-gray-800/60 to-slate-700/80 backdrop-blur-sm">
              <form onSubmit={sendMessage} className="flex items-center gap-2 lg:gap-4">
                <input
                  type="text"
                  value={input}
                  onChange={handleInputChange}
                  placeholder="Tapez votre message..."
                  className="flex-1 border-2 border-slate-600/50 rounded-xl lg:rounded-2xl px-4 lg:px-6 py-3 lg:py-4 focus:outline-none focus:ring-2 focus:ring-amber-500/60 focus:border-amber-500 bg-slate-700/90 backdrop-blur-sm text-amber-100 placeholder-slate-400 font-medium shadow-lg transition-all duration-200 hover:shadow-xl text-sm lg:text-base"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isSending}
                  className="bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 hover:from-amber-600 hover:via-amber-700 hover:to-amber-800 text-slate-900 px-4 lg:px-8 py-3 lg:py-4 rounded-xl lg:rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-bold shadow-xl transition-all duration-300 hover:shadow-2xl hover:scale-105 active:scale-95 text-sm lg:text-base"
                >
                  {isSending ? (
                    <FaSpinner className="animate-spin mr-1 lg:mr-2 text-base lg:text-lg" />
                  ) : (
                    <FaPaperPlane className="mr-1 lg:mr-2 text-base lg:text-lg" />
                  )}
                  <span className="hidden sm:inline">Envoyer</span>
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full p-4">
            <div className="text-center">
              <div className="w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-amber-500/20 to-amber-600/30 rounded-full flex items-center justify-center mx-auto mb-4 lg:mb-6 shadow-xl border border-amber-500/20">
                <span className="text-amber-400 text-2xl lg:text-3xl">📱</span>
              </div>
              <p className="text-slate-300 font-semibold text-lg lg:text-xl">
                {conversations.length > 0 ? "Sélectionnez une conversation" : "Aucune conversation disponible"}
              </p>
              <p className="text-slate-400 text-sm mt-2">
                {conversations.length > 0 ? "pour commencer à discuter" : ""}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemenageurChat;